import { FC, useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "shared/ui/shadcn/dialog";
import { clsxMerge } from "shared/lib/helpers";
import { Skeleton } from "shared/ui/shadcn/skeleton";

import { glencocoClientAPI } from "@/api/glencoco";
import { MeetingDisputeViewI } from "@/api/routes/meeting-qualification";

import { swallow } from "@/api/legacy-swallow";

const QC_LABELS: Record<string, string> = {
  size: "Company Size",
  title: "Title",
  industry: "Industry",
  problem_pain: "Problem/Pain",
  timing_budget: "Timing/Budget",
  bonus_2: "Bonus Criteria 2",
};

interface DisputePreviewModalPropsI {
  isOpen: boolean;
  onClose: () => void;
  campaignId?: string;
  accountId?: string;
}

export const DisputePreviewModal: FC<DisputePreviewModalPropsI> = ({
  isOpen,
  onClose,
  campaignId,
  accountId,
}) => {
  const [dispute, setDispute] = useState<MeetingDisputeViewI | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchDispute = useCallback(async () => {
    if (!campaignId || !accountId) {
      return;
    }

    setIsLoading(true);

    try {
      const API = glencocoClientAPI();
      const resp = await swallow(
        API.meetingQualification.getDispute(campaignId, accountId)
      );

      if (resp.status === 200) {
        setDispute(resp.data.dispute);
      }
    } catch {
      toast.error("Failed to load dispute details");
    } finally {
      setIsLoading(false);
    }
  }, [campaignId, accountId]);

  useEffect(() => {
    if (isOpen) {
      fetchDispute();
    }
  }, [isOpen, fetchDispute]);

  const qcItems = dispute
    ? [
        {
          key: "size",
          isMet: dispute.is_size_met,
          reason: dispute.reason_size_not_met,
        },
        {
          key: "title",
          isMet: dispute.is_title_met,
          reason: dispute.reason_title_not_met,
        },
        {
          key: "industry",
          isMet: dispute.is_industry_met,
          reason: dispute.reason_industry_not_met,
        },
        {
          key: "problem_pain",
          isMet: dispute.is_problem_pain_met,
          reason: dispute.reason_problem_pain_not_met,
        },
        // Bonus criteria only appear when the campaign offers them. A null
        // means "not applicable or not contested", so the row is omitted.
        ...(dispute.is_timing_budget_met !== null
          ? [
              {
                key: "timing_budget",
                isMet: dispute.is_timing_budget_met as boolean,
                reason: dispute.reason_timing_budget_not_met,
              },
            ]
          : []),
        ...(dispute.is_bonus_2_met != null
          ? [
              {
                key: "bonus_2",
                isMet: dispute.is_bonus_2_met,
                reason: dispute.reason_bonus_2_not_met,
              },
            ]
          : []),
      ]
    : [];

  const isSystemHold = dispute?.source === "system_duplicate_phone";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {isSystemHold
              ? "Payout hold: duplicate phone number"
              : "Dispute details"}
          </DialogTitle>

          <DialogDescription>
            {isSystemHold
              ? "This meeting's payout was automatically held because the prospect's phone number recently had a paid meeting on another account. The workspace admin will review it - no action is needed from you."
              : "The AE has disputed this meeting qualification. Below are the details of the dispute."}
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-3">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-12 w-full rounded-lg" />
              <Skeleton className="h-12 w-full rounded-lg" />
              <Skeleton className="h-12 w-full rounded-lg" />
              <Skeleton className="h-12 w-full rounded-lg" />
            </div>

            <div className="flex flex-col gap-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-64" />
            </div>

            <div className="flex flex-col gap-2">
              <Skeleton className="h-4 w-14" />
              <Skeleton className="h-16 w-full" />
            </div>
          </div>
        )}

        {!isLoading && !dispute && (
          <div className="py-8 text-center">
            <p className="text-black/60 typography-body-3">
              No dispute found for this meeting.
            </p>
          </div>
        )}

        {!isLoading && dispute && isSystemHold && (
          <div className="flex flex-col gap-5">
            <div className="rounded-lg border border-[#FDE68A] bg-[#FEF3C7] p-4">
              <p className="typography-body-4-medium">Automatic payout hold</p>

              <p className="mt-1 break-words text-black/60 typography-body-4">
                {dispute.evidence_notes ??
                  "This meeting's payout was automatically held because the prospect's phone number recently had a paid meeting on another account."}
              </p>
            </div>

            {dispute.status !== "pending" && (
              <div className="rounded-lg bg-black/5 p-4">
                <p className="typography-body-4-medium">
                  Resolution:{" "}
                  {dispute.status === "confirmed"
                    ? "Duplicate confirmed, not paid"
                    : "Payout released"}
                </p>

                {dispute.resolution_notes && (
                  <p className="mt-1 text-black/60 typography-body-4">
                    {dispute.resolution_notes}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {!isLoading && dispute && !isSystemHold && (
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-3">
              <p className="typography-body-4-medium">Qualification Criteria</p>

              {qcItems.map((item) => (
                <div
                  key={item.key}
                  className={clsxMerge(
                    "rounded-lg border p-3",
                    !item.isMet
                      ? "border-[rgb(251,95,95)] bg-[rgb(251,95,95)]/5"
                      : "border-black/10"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={clsxMerge(
                        "inline-flex h-5 w-5 items-center justify-center rounded-full typography-body-5",
                        item.isMet
                          ? "bg-green-100 text-green-600"
                          : "bg-red-100 text-red-600"
                      )}
                    >
                      {item.isMet ? "\u2713" : "\u2717"}
                    </span>

                    <span className="typography-body-4-medium">
                      {QC_LABELS[item.key]}
                    </span>
                  </div>

                  {!item.isMet && item.reason && (
                    <p className="mt-2 break-all pl-7 text-black/60 typography-body-4">
                      {item.reason}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {dispute.evidence_link_url && (
              <div>
                <p className="typography-body-4-medium">Evidence</p>

                <a
                  href={dispute.evidence_link_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 block text-[#4474E3] underline typography-body-4"
                >
                  {dispute.evidence_link_url}
                </a>
              </div>
            )}

            {dispute.evidence_notes && (
              <div>
                <p className="typography-body-4-medium">Notes</p>

                <p className="mt-1 text-black/60 typography-body-4">
                  {dispute.evidence_notes}
                </p>
              </div>
            )}

            {dispute.status !== "pending" && (
              <div className="rounded-lg bg-black/5 p-4">
                <p className="typography-body-4-medium">
                  Resolution:{" "}
                  {dispute.status === "confirmed" ? "Approved" : "Rejected"}
                </p>

                {dispute.resolution_notes && (
                  <p className="mt-1 text-black/60 typography-body-4">
                    {dispute.resolution_notes}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
